import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import * as API from '../../API';

import { CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import type EFactBatch from '../../Models/EFactBatch';
import EFactBatchStatus from '../../Models/EFactBatchStatus';
import Styles from './PendingBatches.module.scss';

function PendingBatches(): React.ReactElement {
    const { t } = useTranslation();

    const [batches, setBatches] = useState<EFactBatch[]>([]);
    const [firstPending, setFirstPending] = useState(-1);
    const [loading, setLoading] = useState(true);

    const calcSendingDateAndSetState = (res: any): void => {
        const checkedIOs: string[] = [];
        let pendingFound = false;
        res.Batches.forEach((b: EFactBatch, i: number) => {
            const io = b.IO;
            b.SendingDate = 'Now';
            if (b.StatusID === EFactBatchStatus.Running) {
                checkedIOs.push(io);
                return;
            }
            if (!res.AlreadyInvoicedToday.includes((x: string) => x === io)) {
                if (!checkedIOs.includes(io)) {
                    b.SendingDate = 'Today';
                }
                else {
                    const prev = checkedIOs.filter((x) => x === io).length;
                    b.SendingDate = prev === 1 ? 'Tomorrow' : 'AfterTomorrow';
                }
            }
            else {
                b.SendingDate = 'AlreadySentToThisIOToday';
            }
            checkedIOs.push(io);
            if (!pendingFound) {
                pendingFound = true;
                setFirstPending(i);
            }
        });
        setBatches(res.Batches);
        setLoading(false);
    };

    const getBatches = (): void => {
        setLoading(true);
        API.eFact.getPendingEFactBatches()
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Issue with getPendingBatches:', res);
                }
                calcSendingDateAndSetState(res);
            })
            .catch((err) => {
                console.error('Could not get pending EFact Batches:', err);
            });
    };

    useEffect(getBatches, []); // No dependencies -> run useEffect once!

    const onBatchUpdate = (res: any): void => calcSendingDateAndSetState(res);

    return (
        <div>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {/* TextAlign left is necessary here to overwrite datatables.css overzealous styling... */}
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'left !important' }}>{t('StartDate')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('EndDate')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('IO')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('AskedAmount')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>{t('NumberOfOrders')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('Created')} {t('By')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('Sending')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('Options')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={Styles.rows}>
                        { loading
                            ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                )
                            : batches.map((x, i) => <BatchRow key={x.ID} batch={x} firstPending={i === firstPending} last={i === batches.length - 1} onUpdate={onBatchUpdate} onCancel={getBatches} />) }
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

function BatchRow({ batch, firstPending, last, onUpdate, onCancel }: Readonly<{
    batch: EFactBatch; firstPending: boolean; last: boolean; onUpdate: Function; onCancel: Function;
}>): React.ReactElement {
    const { t } = useTranslation();

    const moveFirst = (): void => {
        API.eFact.moveBatchFirstInQueue(batch.ID)
            .then((res) => onUpdate(res))
            .catch((err) => console.error(err));
    };

    const moveUp = (): void => {
        API.eFact.moveBatchUpInQueue(batch.ID)
            .then((res) => onUpdate(res))
            .catch((err) => console.error(err));
    };

    const moveDown = (): void => {
        API.eFact.moveBatchDownInQueue(batch.ID)
            .then((res) => onUpdate(res))
            .catch((err) => console.error(err));
    };

    const cancel = (): void => {
        API.eFact.cancelRunningEFactBatch(batch.ID)
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Could not cancel running Efact batch:', res);
                }
                onCancel();
            })
            .catch((err) => console.error(err));
    };

    const getBackgroundColor = () => {
        if (batch.StatusID == EFactBatchStatus.Running) {
            return '#7bc17b !important';
        }
        else if (batch.StatusID == EFactBatchStatus.Waiting) {
            return '#7bb7c1 !important';
        }
        return 'initial';
    };

    return (
        <TableRow sx={{ backgroundColor: getBackgroundColor() }}>
            <TableCell>{batch.PeriodStart_String}</TableCell>
            <TableCell>{batch.PeriodEnd_String}</TableCell>
            <TableCell>{batch.IO}</TableCell>
            <TableCell sx={{ textAlign: 'right' }}>&euro;{batch.IOSummary.AskedAmount}</TableCell>
            <TableCell sx={{ textAlign: 'right' }}>{batch.OrderCount}</TableCell>
            <TableCell>{batch.Created_String}</TableCell>
            <TableCell>{t(batch.SendingDate)}</TableCell>
            <TableCell>
                <IconButton aria-label="expand row" size="small" disabled={firstPending || (batch.StatusID === EFactBatchStatus.Running || batch.StatusID == EFactBatchStatus.Waiting)} onClick={moveUp}>
                    <span className="material-symbols-outlined">keyboard_arrow_up</span>
                </IconButton>
                <IconButton aria-label="expand row" size="small" disabled={last || (batch.StatusID === EFactBatchStatus.Running || batch.StatusID == EFactBatchStatus.Waiting)} onClick={moveDown}>
                    <span className="material-symbols-outlined">keyboard_arrow_down</span>
                </IconButton>
                <IconButton aria-label="expand row" size="small" disabled={firstPending || (batch.StatusID === EFactBatchStatus.Running || batch.StatusID == EFactBatchStatus.Waiting)} onClick={moveFirst}>
                    <span className="material-symbols-outlined">keyboard_double_arrow_up</span>
                </IconButton>
                <IconButton aria-label="expand row" size="small" onClick={cancel}>
                    <span className="material-symbols-outlined">block</span>
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

export default PendingBatches;
